import { isText, toObject, toRexExp, toNumber } from './bits'
import { assign, isArray, identity, isRegExp, isFinite, isPlainObject } from 'lodash-es'

import getEnv from './get-env'
import browser from './browser'
import StrictObj from './strict-obj'
import attachGetters from './attach-getters'

const getEnvBool = getEnv

const getEnvArray = (conf) => getEnv(assign({
  cast: toObject,
  check: isArray
}, conf))

const getEnvObj = (conf) => getEnv(assign({
  cast: toObject,
  check: isPlainObject
}, conf))

const getEnvNum = (conf) => getEnv(assign({
  cast: toNumber,
  check: isFinite
}, conf))

const getEnvRE = (conf) => getEnv(assign({
  cast: toRexExp,
  check: isRegExp
}, conf))

const getEnvStr = (conf) => getEnv(assign({
  cast: identity,
  check: isText
}, conf))

const envRaw = attachGetters(StrictObj({}))
export default envRaw

// envs
envRaw.NODE_ENV = process.env.NODE_ENV

// modes
envRaw.debugMode = getEnvBool({
  def: false,
  key: 'app-debug',
  msg: `${__filename} gallery will :not: turn into global debug mode`
})

envRaw.liveEditMode = getEnvBool({
  def: false,
  key: 'live-edit-mode',
  msg: `${__filename} gallery will :not: turn into global live-edit mode`
})

envRaw.devMode = getEnvBool({
  def: /^(dev|test)/i.test(process.env.NODE_ENV),
  key: 'dev-mode',
  msg: `${__filename} gallery will :not: turn into global dev mode`
})

// log levels
envRaw.logLevel = getEnvStr({
  def: envRaw.debugMode
    ? 'debug'
    : envRaw.devMode
      ? 'info'
      : 'error',
  key: 'log-level',
  msg: (val) => `${__filename} gallery will use ${val} log level`
})

envRaw.notificationLevel = getEnvStr({
  def: envRaw.devMode
    ? 'debug'
    : 'info',
  key: 'notification-level',
  msg: (val) => `${__filename} gallery will use ${val} notification level`
})

// show debug messages
envRaw.showsEmbedResizeDebug = getEnvBool({
  def: envRaw.debugMode,
  key: 'shows-embed-resize-debug',
  msg: `${__filename} gallery will :not: show embed-resize module debug`
})

envRaw.showsViewportResizeDebug = getEnvBool({
  def: envRaw.debugMode,
  key: 'shows-viewport-resize-debug',
  msg: `${__filename} gallery will :not: show viewport-resize module debug`
})

envRaw.showsPageScrollDebug = getEnvBool({
  def: envRaw.debugMode,
  key: 'shows-page-scroll-debug',
  msg: `${__filename} gallery will :not: show page-scroll module debug`
})

envRaw.showsEntryEmitterDebug = getEnvBool({
  def: envRaw.debugMode,
  key: 'shows-entry-emitter-debug',
  msg: `${__filename} gallery will :not: show entry-emitter module debug`
})

envRaw.showsOnClickDebug = getEnvBool({
  def: envRaw.debugMode,
  key: 'shows-on-click-debug',
  msg: `${__filename} gallery will :not: show on-click module debug`
})

envRaw.showsRootResizeDebug = getEnvBool({
  def: envRaw.debugMode,
  key: 'shows-root-resize-debug',
  msg: `${__filename} gallery will :not: show root-resize module debug`
})

envRaw.showsFixPostDebug = getEnvBool({
  def: envRaw.debugMode,
  key: 'shows-fix-post-debug',
  msg: `${__filename} gallery will :not: show fix-post module debug`
})

envRaw.showsDefineDebug = getEnvBool({
  def: envRaw.debugMode,
  key: 'shows-define-debug',
  msg: `${__filename} gallery will :not: show define module debug`
})

envRaw.showsImgQueueDebug = getEnvBool({
  def: envRaw.debugMode,
  key: 'shows-img-queue-debug',
  msg: `${__filename} gallery will :not: show img-queue module debug`
})

envRaw.showsPostInfoDebug = getEnvBool({
  def: envRaw.debugMode,
  key: 'shows-post-info-debug',
  msg: `${__filename} gallery will :not: show post-info module debug`
})

envRaw.showsHosthDebug = getEnvBool({
  def: envRaw.debugMode,
  key: 'shows-hosth-debug',
  msg: `${__filename} gallery will :not: show "hosth" module debug`
})

envRaw.showsPropertiesDebug = getEnvBool({
  def: envRaw.debugMode,
  key: 'shows-properties-debug',
  msg: `${__filename} gallery will :not: show "properties" module debug`
})

envRaw.showsScrollDetectorDebug = getEnvBool({
  def: envRaw.debugMode,
  key: 'shows-scroll-detector-debug',
  msg: `${__filename} gallery will :not: show "scroll-detector" module debug`
})

envRaw.showsTransitionDebug = getEnvBool({
  def: envRaw.debugMode,
  key: 'shows-transition-debug',
  msg: `${__filename} gallery will :not: show "on-transition-end" module debug`
})

envRaw.showsAngularDebug = getEnvBool({
  def: envRaw.debugMode,
  key: 'shows-angular-debug',
  msg: `${__filename} gallery will :not: show "angular" debug`
})

envRaw.showsAngularComponentDebug = getEnvBool({
  def: envRaw.showsAngularDebug,
  key: 'shows-angular-component-debug',
  msg: `${__filename} gallery will :not: show "angular/component" debug`
})

envRaw.showsAngularDirectiveDebug = getEnvBool({
  def: envRaw.showsAngularDebug,
  key: 'shows-angular-directive-debug',
  msg: `${__filename} gallery will :not: show "angular/directive" debug`
})

envRaw.showsEmitterDebug = getEnvBool({
  def: envRaw.debugMode,
  key: 'shows-emitter-debug',
  msg: `${__filename} gallery will :not: show "emitter" module debug`
})

envRaw.showsElementQueriesDebug = getEnvBool({
  def: envRaw.debugMode,
  key: 'shows-element-queries-debug',
  msg: `${__filename} gallery will :not: show "element-queries" module debug`
})

envRaw.showsAdvancedHeightCacheDebug = getEnvBool({
  def: envRaw.debugMode,
  key: 'shows-advanced-width-cache-debug',
  msg: `${__filename} gallery will :not: show "shows-advanced-width-cache" component debug`
})

envRaw.showsFontEmitterDebug = getEnvBool({
  def: envRaw.debugMode,
  key: 'shows-font-emitter-debug',
  msg: `${__filename} gallery will :not: show "font-emitter" component debug`
})

envRaw.showsVoteFrameDebug = getEnvBool({
  def: envRaw.debugMode,
  key: 'shows-vote-frame-debug',
  msg: `${__filename} gallery will :not: show vote-frame debug`
})

// show visual debug
envRaw.showsVisualDebug = getEnvBool({
  def: envRaw.debugMode,
  key: 'shows-visual-debug',
  msg: `${__filename} gallery will :not: show visual debug`
})

envRaw.showsModalBounds = getEnvBool({
  def: envRaw.showsVisualDebug,
  key: 'shows-modal-bounds',
  msg: `${__filename} gallery will :not: show modal bounds`
})

// use
envRaw.useSelfKiller = getEnvBool({
  def: true,
  key: 'use-self-killer',
  msg: `${__filename} gallery will :not: use self-killer module`
})

envRaw.useCallTracker = getEnvBool({
  def: envRaw.debugMode,
  key: 'use-call-tracker',
  msg: `${__filename} gallery will :not: use call-tracker module`
})

envRaw.useDebugRequest = getEnvBool({
  def: envRaw.debugMode,
  key: 'use-debug-request',
  msg: `${__filename} gallery will :not: use debug-request module`
})

envRaw.useProfile = getEnvBool({
  def: envRaw.debugMode,
  key: 'use-profile',
  msg: `${__filename} gallery will :not: use profile module`
})

envRaw.useRoeStuckDetector = getEnvBool({
  def: envRaw.debugMode,
  key: 'roe-stuck-detector',
  msg: `${__filename} gallery will :not: use roe stuck detector`
})

envRaw.useNativeAttrsObserver = getEnvBool({
  def: !!window.MutationObserver,
  key: 'use-native-attrs-observer',
  msg: `${__filename} gallery will :not: use native attributes observer`
})

envRaw.useNativeSmoothScroll = getEnvBool({
  def: document.documentElement.style.hasOwnProperty('scrollBehavior'),
  key: 'use-native-smooth-scroll',
  msg: `${__filename} gallery will :not: use native smooth scroll`
})

envRaw.useNativeResizeObserver = getEnvBool({
  def: false,
  key: 'use-native-resize-observer',
  msg: `${__filename} gallery will :not: use native resize observer`
})

envRaw.useDebugObj = getEnvBool({
  def: envRaw.devMode,
  key: 'use-debug-obj',
  msg: `${__filename} gallery will :not: use debug-obj helper`
})

envRaw.useDebugRaw = getEnvBool({
  def: envRaw.devMode,
  key: 'use-debug-raw',
  msg: `${__filename} gallery will :not: use debug-raw helper`
})

envRaw.useDebugFields = getEnvBool({
  def: envRaw.devMode,
  key: 'use-debug-fields',
  msg: `${__filename} gallery will :not: use debug-fields helper`
})

envRaw.useTransformMode = getEnvBool({
  def: browser.likeDesktop,
  key: 'use-transform-mode',
  msg: `${__filename} gallery will :not: use transform mode instead of scroll mode`
})

envRaw.useActualToMeasureViewport = getEnv({
  def: !document.doctype,
  key: 'use-actual-to-measure-viewport',
  msg: `${__filename} gallery will :not: use the actual package to measure viewport`
})

// bootstrap
envRaw.useBootstrap = getEnvBool({
  def: true,
  key: 'use-bootstrap',
  msg: `${__filename} gallery will :not: use bootstrap module`
})

envRaw.bootstrapOverride = getEnvObj({
  def: null,
  key: 'bootstrap-override',
  msg: (val) => `${__filename} gallery will use ${val} to override bootstrap`
})

// backend
envRaw.backendDelay = getEnvNum({
  def: null,
  key: 'backend-delay',
  msg: (val) => `${__filename} gallery-backend will use [delay=${val}]`
})

envRaw.backendLimit = getEnvNum({
  def: null,
  key: 'backend-limit',
  msg: (val) => `${__filename} gallery-backend will use [postLimit=${val}]`
})

envRaw.backendAbove = getEnvNum({
  def: null,
  key: 'backend-above',
  msg: (val) => `${__filename} gallery-backend will use [aboveLimit=${val}]`
})

envRaw.backendTop = getEnvNum({
  def: null,
  key: 'backend-top',
  msg: (val) => `${__filename} gallery-backend will use [topLimit=${val}]`
})

envRaw.backendClearLS = getEnvRE({
  def: null,
  key: 'backend-clear-ls',
  msg: (val) => `${__filename} gallery-backend will clear LS by [re=${val}]`
})

envRaw.backendSeed = getEnvStr({
  def: null,
  key: 'backend-seed',
  msg: (val) => `${__filename} gallery-backend will use [seed=${val}]`
})

// overrides
envRaw.properties = getEnvObj({
  def: null,
  key: 'properties',
  msg: (val) => `${__filename} gallery will override [properties=${val}]`
})

// fetch delays
envRaw.aboveTimeoutDelays = getEnvArray({
  def: null,
  key: 'above-timeout-delays',
  msg: (val) => `${__filename} gallery will use [aboveTimeoutDelays=${val}]`
})

envRaw.belowIntervalDelay = getEnvNum({
  def: null,
  key: 'below-interval-delay',
  msg: (val) => `${__filename} gallery will use [belowIntervalDelay=${val}]`
})

// clickEvent
envRaw.clickEvent = getEnvStr({
  def: browser.likeMobile ? 'click' : 'mousedown',
  key: 'click-event',
  msg: (val) => `${__filename} gallery will use [clickEvent=${val}]`
})

envRaw.listSelectClickEvent = getEnvStr({
  def: 'click',
  key: 'list-select-click-event',
  msg: (val) => `${__filename} gallery will use [listSelectClickEvent=${val}]`
})

envRaw.gallerySharingClickEvent = getEnvStr({
  def: 'click',
  key: 'gallery-sharing-click-event',
  msg: (val) => `${__filename} gallery will use [gallerySharingClickEvent=${val}]`
})

envRaw.galleryCodeWizardClickEvent = getEnvStr({
  def: 'click',
  key: 'gallery-code-wizard-click-event',
  msg: (val) => `${__filename} gallery will use [galleryCodeWizardClickEvent=${val}]`
})

envRaw.adminToolbarClickEvent = getEnvStr({
  def: 'click',
  key: 'admin-toolbar-click-event',
  msg: (val) => `${__filename} gallery will use [adminToolbarClickEvent=${val}]`
})

// my-isotope
envRaw.carouselScrollSpeed = getEnvNum({
  def: 3,
  key: 'carousel-scroll-speed',
  msg: (val) => `${__filename} gallery will override [carouselScrollSpeed=${val}] px/ms`
})

envRaw.carouselScrollEaseFunc = getEnvStr({
  def: 'easeInOutCubic',
  key: 'carousel-scroll-ease-func',
  msg: (val) => `${__filename} gallery will override [carouselScrollEaseFunc=${val}]`
})

envRaw.masonryScrollSpeed = getEnvNum({
  def: 2,
  key: 'masonry-scroll-speed',
  msg: (val) => `${__filename} gallery will override [masonryScrollSpeed=${val}] px/ms`
})

envRaw.masonryScrollEaseFunc = getEnvStr({
  def: 'easeInOutQuad',
  key: 'masonry-scroll-ease-func',
  msg: (val) => `${__filename} gallery will override [masonryScrollEaseFunc=${val}]`
})

// gallery-slider
envRaw.sliderScrollSpeed = getEnvNum({
  def: 0.5,
  key: 'slider-scroll-speed',
  msg: (val) => `${__filename} gallery will override [sliderScrollSpeed=${val}] px/ms`
})

envRaw.sliderScrollEaseFunc = getEnvStr({
  def: 'easeInOutCubic',
  key: 'slider-scroll-ease-func',
  msg: (val) => `${__filename} gallery will override [sliderScrollEaseFunc=${val}]`
})

// gallery-modal-list

envRaw.modalListCanBePreloaded = getEnvBool({
  def: true,
  key: 'modal-list-can-be-preloaded',
  msg: `${__filename} gallery will :not: allow to preload the modal list`
})
